import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, Card, CardContent, CircularProgress, Divider } from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';   // Bordro ikonu
import PaymentsIcon from '@mui/icons-material/Payments';             // Maaş ikonu
import DescriptionIcon from '@mui/icons-material/Description';        // Bordro Görüntüleme ikonu
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';        // Avans ikonu
import { useTranslation } from 'react-i18next';

const PayrollPaymentsPage: React.FC = () => {
  const { t } = useTranslation(['humanResources', 'common2']);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            marginLeft: '10px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555'
            }
          }
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <RequestQuoteIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                {hr('pages.payroll.title', 'Payroll and Payments System')}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
              {hr('pages.payroll.subtitle', 'This page is under construction. The payroll and payments module will be available soon.')}
            </Typography>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.developmentProcess', 'Development Process')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.analysis', 'Analysis')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={90} color="success" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.inProgress', 'In Progress')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.design', 'Design')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={10} color="primary" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.initialStage', 'Initial Stage')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.development', 'Development')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={0} color="warning" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.notStarted', 'Not Started')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.plannedFeatures', 'Module Features (Planned)')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <PaymentsIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.payroll.features.salaryTitle', 'Salary Calculation and Management')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.payroll.features.salaryDesc', 'Management of employee salary details, deductions, and additional payments with automated calculations.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <DescriptionIcon sx={{ fontSize: 40, mb: 1, color: 'info.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.payroll.features.payslipTitle', 'Payslip Viewing (Self-Service)')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.payroll.features.payslipDesc', 'Allows employees to securely view their own payslips.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <AttachMoneyIcon sx={{ fontSize: 40, mb: 1, color: 'warning.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.payroll.features.expenseTitle', 'Advance and Expense Management')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.payroll.features.expenseDesc', 'Creation and approval of advance requests and tracking expenses (integrated with Pre-Accounting).')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'rgba(156, 39, 176, 0.04)' }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.payroll.whyTitle', 'Why a Payroll and Payments System?')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" paragraph>
                {hr('pages.payroll.whyIntro', 'Accurate and timely payments are fundamental for employee satisfaction and legal compliance. With this system:')}
              </Typography>
              
              <Box component="ul" sx={{ pl: 2 }}>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.payroll.benefits.errorsReduced', 'Payroll calculation errors are minimized.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.payroll.benefits.compliance', 'Compliance with legal regulations is ensured (tax, insurance, etc.).')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.payroll.benefits.automation', 'Payment processes become automated and faster.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.payroll.benefits.access', 'Employees can easily access payroll information.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.payroll.benefits.reporting', 'Reporting and analysis capabilities improve.')}</Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {hr('pages.payroll.conclusion', 'Our payroll and payments module will increase your HR department efficiency by simplifying complex calculations, ensuring legal compliance, and providing self-service features.')}
              </Typography>
            </Paper>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
              <Button variant="contained" color="primary" disabled>
                {hr('pages.shared.moduleAvailableWhenReady', 'The Module Will Be Available When Ready')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PayrollPaymentsPage; 